import {
  PMA_PROVISION_REPORT_FILENAME,
  buildProvisionReportCsv,
} from '../domain/pma/pmaProvisionReport';

function formatFailedStep(step) {
  if (!step) return '';
  const where = step.host ? `${step.host}${step.port ? `:${step.port}` : ''}` : '';
  const parts = [
    `FAIL [${step.step}]`,
    where,
    step.detail,
    step.command ? `cmd: ${step.command}` : null,
    step.stderr ? `stderr: ${step.stderr}` : null,
  ].filter(Boolean);
  return parts.join('\n  ');
}

function downloadBase64Report(report) {
  if (!report?.base64) return;
  const binary = atob(report.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: report.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = report.filename || 'lab_linux_vnc_credentials.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function downloadCombinedCsv(users = []) {
  if (!users.length) return;
  const blob = new Blob([buildProvisionReportCsv(users)], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = PMA_PROVISION_REPORT_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function PmaProvisionDebug({ result }) {
  if (!result) return null;

  const users = result.sms?.users || result.users || [];
  const issues = result.issues || result.sms?.issues || result.debug?.issues || [];
  const report = result.report || result.sms?.report || null;
  const hasSteps = users.some((u) => Array.isArray(u.steps) && u.steps.length);
  const hasIssues = issues.length > 0;
  const hasCreds = users.some((u) => u.linuxPassword || u.vncPassword || u.vncPort);

  if (!hasSteps && !hasIssues && !result.message && !report && !hasCreds) return null;

  const failedFromUsers = users.flatMap((user) =>
    (user.steps || [])
      .filter((s) => s && s.ok === false)
      .map((s) => ({
        ...s,
        userId: user.userId,
        rowNumber: user.rowNumber,
        email: user.emailId,
      }))
  );

  return (
    <div className="space-y-3" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-medium theme-text">Provision result</p>
        {users.length > 0 && (
          <button
            type="button"
            className="text-sm underline theme-text"
            onClick={() => downloadCombinedCsv(users)}
          >
            Download Linux/VNC credentials CSV ({users.length} user{users.length === 1 ? '' : 's'})
          </button>
        )}
        {users.length === 0 && report?.base64 && (
          <button
            type="button"
            className="text-sm underline theme-text"
            onClick={() => downloadBase64Report(report)}
          >
            Download Linux/VNC credentials Excel
          </button>
        )}
      </div>
      {result.message && <p className="text-sm theme-text-secondary">{result.message}</p>}
      {report?.filename && (
        <p className="text-xs theme-text-secondary">
          Report: {report.filename}
          {report.rowCount != null ? ` · ${report.rowCount} row(s)` : ''}
        </p>
      )}

      {hasCreds && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs theme-text">
            <thead>
              <tr className="text-left theme-text-secondary">
                <th className="pr-3 py-1">userId</th>
                <th className="pr-3 py-1">linuxPassword</th>
                <th className="pr-3 py-1">vncPassword</th>
                <th className="pr-3 py-1">vncPort</th>
                <th className="pr-3 py-1">status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id || `${user.rowNumber}-${user.userId}`}>
                  <td className="pr-3 py-1 font-mono">{user.userId}</td>
                  <td className="pr-3 py-1 font-mono">{user.linuxPassword || '—'}</td>
                  <td className="pr-3 py-1 font-mono">{user.vncPassword || '—'}</td>
                  <td className="pr-3 py-1 font-mono">{user.vncPort || '—'}</td>
                  <td className="pr-3 py-1">{user.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(hasIssues || failedFromUsers.length > 0) && (
        <div
          className="p-3 rounded-lg space-y-2 text-sm whitespace-pre-wrap"
          style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}
        >
          <p className="font-medium">Real errors</p>
          {issues.map((item) => (
            <div key={`issue-${item.rowNumber}-${item.userId || item.email || 'x'}`} className="space-y-1">
              <p>
                Row {item.rowNumber}
                {item.userId ? ` · ${item.userId}` : ''}
                {item.step ? ` · [${item.step}]` : ''}
                {item.host ? ` · ${item.host}${item.port ? `:${item.port}` : ''}` : ''}
              </p>
              <p>{item.error}</p>
              {item.command && <p className="font-mono text-xs">cmd: {item.command}</p>}
              {item.stderr && <p className="font-mono text-xs">stderr: {item.stderr}</p>}
              {(item.failedSteps || []).map((fs, idx) => (
                <pre key={`fs-${idx}`} className="font-mono text-xs overflow-x-auto m-0">
                  {formatFailedStep(fs)}
                </pre>
              ))}
            </div>
          ))}
          {!issues.length &&
            failedFromUsers.map((step, idx) => (
              <pre key={`fail-${idx}`} className="font-mono text-xs overflow-x-auto m-0">
                {step.userId ? `${step.userId}\n` : ''}
                {formatFailedStep(step)}
              </pre>
            ))}
        </div>
      )}

      {users.map((user) => {
        const steps = user.steps || [];
        if (!steps.length && user.status === 'created' && !user.error) {
          return (
            <div key={user.id || `${user.rowNumber}-${user.userId}`} className="text-sm theme-text-secondary">
              {user.userId || user.emailId}: {user.dryRun ? 'dry-run OK' : 'created'}
              {user.hosts?.length ? ` on ${user.hosts.join(', ')}` : ''}
              {user.vncPort ? ` · VNC ${user.vncPort}` : ''}
            </div>
          );
        }
        if (!steps.length) return null;
        return (
          <details
            key={user.id || `${user.rowNumber}-${user.userId}`}
            className="text-sm rounded-lg p-3"
            style={{ border: '1px solid var(--color-border)' }}
            open={user.status === 'failed' || steps.some((s) => s.ok === false)}
          >
            <summary className="cursor-pointer theme-text font-medium">
              {user.userId || user.emailId || `row ${user.rowNumber}`}
              {' · '}
              <span className="theme-text-secondary">
                {user.status}
                {user.step ? ` @ ${user.step}` : ''}
                {user.dryRun ? ' (dry-run)' : ''}
              </span>
            </summary>
            {user.error && (
              <p className="mt-2 whitespace-pre-wrap" style={{ color: 'var(--color-error)' }}>
                {user.error}
              </p>
            )}
            <ol className="mt-2 space-y-2 font-mono text-xs theme-text-secondary list-decimal pl-4">
              {steps.map((step, idx) => (
                <li key={`${step.step}-${idx}-${step.at || idx}`}>
                  <div style={{ color: step.ok === false ? 'var(--color-error)' : 'inherit' }}>
                    <div>
                      {step.ok === false ? 'FAIL' : 'ok'} {step.step}
                      {step.host ? ` ${step.host}${step.port ? `:${step.port}` : ''}` : ''}
                      {step.durationMs != null ? ` (${step.durationMs}ms)` : ''}
                    </div>
                    {step.detail && <div className="whitespace-pre-wrap">{step.detail}</div>}
                    {step.command && <div>cmd: {step.command}</div>}
                    {step.stderr && <div>stderr: {step.stderr}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </details>
        );
      })}
    </div>
  );
}
